import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  Chip,
  Divider,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalPharmacy as PharmacyIcon
} from '@mui/icons-material';
import { drugAPI } from '../../api/drugs';
import { patientAPI } from '../../api/patients';
import { appointmentAPI } from '../../api/appointments';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/apiConfig';
import { toast } from 'react-toastify';
import { openPrescriptionPDF } from '../../utils/pdfPrescription';
import { icd10API } from '../../api/icd10';
import { formatFailedActionMessage } from '../../utils/uiMessages';

const AddPrescription = ({ open, onClose, onAdd }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    patient: '',
    appointment: '',
    doctor: '',
    diagnosis: '',
    medications: [{
      drug: '',
      dosage: '',
      frequency: '',
      duration: '',
      instructions: '',
      quantity: 1,
      unit: 'tablets',
      beforeMeal: false,
      afterMeal: false
    }],
    instructions: '',
    followUpDate: '',
    notes: ''
  });
  
  const [drugs, setDrugs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [icd10Options, setIcd10Options] = useState([]);
  const [icd10Loading, setIcd10Loading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    if (open) {
      loadDrugs();
      loadPatients();
      loadDoctors();
      loadAppointments();
    }
  }, [open]);
  
  // Default doctor to current user when dialog opens and doctors are loaded
  useEffect(() => {
    if (!open || doctors.length === 0) return;
    const defaultDoctorId = user?._id && doctors.some(d => d._id === user._id)
      ? user._id
      : (doctors[0]?._id || '');
    setFormData(prev => (prev.doctor ? prev : { ...prev, doctor: defaultDoctorId }));
  }, [open, user?._id, doctors]);
  
  const loadDrugs = async () => {
    try {
      const response = await drugAPI.getDrugs({ limit: 1000, isActive: true });
      setDrugs(response.data.docs || []);
    } catch (err) {
      console.error('Error loading drugs:', err);
    }
  };
  
  const loadPatients = async () => {
    setLoadingPatients(true);
    try {
      const response = await patientAPI.getPatients({ limit: 1000, status: 'active' });
      setPatients(response.data || []);
    } catch (err) {
      console.error('Error loading patients:', err);
    } finally {
      setLoadingPatients(false);
    }
  };
  
  const loadAppointments = async () => {
    setLoadingAppointments(true);
    try {
      const response = await appointmentAPI.getUpcomingAppointments();
      // Appointments API returns data in response.data.data, not response.data.docs
      setAppointments(response.data || []);
    } catch (err) {
      console.error('Error loading appointments:', err);
    } finally {
      setLoadingAppointments(false);
    }
  };
  
  const loadDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await api.get('/users/doctors');
      const list = response.data?.data || response.data || [];
      setDoctors(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading doctors:', err);
    } finally {
      setLoadingDoctors(false);
    }
  };
  
  const loadPatientAppointments = async (patientId) => {
    if (!patientId) {
      loadAppointments(); // Load all appointments if no patient selected
      return;
    }
    setLoadingAppointments(true);
    try {
      const response = await appointmentAPI.getPatientAppointments(patientId);
      // Appointments API returns data in response.data.data, not response.data.docs
      setAppointments(response.data || []);
    } catch (err) {
      console.error('Error loading patient appointments:', err);
    } finally {
      setLoadingAppointments(false);
    }
  };
  
  // Get selected patient name for display
  const getSelectedPatientName = () => {
    if (!formData.patient) return '';
    const patient = patients.find(p => p._id === formData.patient);
    return patient ? patient.name : '';
  };
  
  // Format appointment display
  const formatAppointmentDisplay = (appointment) => {
    try {
      const date = new Date(appointment.appointmentDate);
      const dateStr = date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} at ${timeStr} (${appointment.duration} min)`;
    } catch (error) {
      console.error('Error formatting appointment date:', error);
      return 'Invalid date';
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // If patient is selected, load their appointments
    if (field === 'patient') {
      loadPatientAppointments(value);
    }
  };
  
  const handleMedicationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index ? { ...med, [field]: value } : med
      )
    }));
  };
  
  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, {
        drug: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
        quantity: 1,
        unit: 'tablets',
        beforeMeal: false,
        afterMeal: false
      }]
    }));
  };
  
  const removeMedication = (index) => {
    if (formData.medications.length > 1) {
      setFormData(prev => ({
        ...prev,
        medications: prev.medications.filter((_, i) => i !== index)
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.patient) newErrors.patient = 'Patient is required';
    if (!formData.doctor) newErrors.doctor = 'Prescribing doctor is required';
    if (!formData.diagnosis) newErrors.diagnosis = 'Diagnosis is required';
    
    // Validate medications
    formData.medications.forEach((med, index) => {
      if (!med.drug) newErrors[`medication_${index}_drug`] = 'Drug is required';
      if (!med.dosage) newErrors[`medication_${index}_dosage`] = 'Dosage is required';
      if (!med.frequency) newErrors[`medication_${index}_frequency`] = 'Frequency is required';
      if (!med.duration) newErrors[`medication_${index}_duration`] = 'Duration is required';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Clean up and normalize medications: ensure dosage/frequency/duration are text from form, drug is id only
      const cleanMedications = formData.medications
        .filter(med => med.drug && med.dosage && med.frequency && med.duration)
        .map(med => ({
          drug: (typeof med.drug === 'object' && med.drug != null && med.drug._id) ? med.drug._id : med.drug,
          dosage: String(med.dosage ?? '').trim(),
          frequency: String(med.frequency ?? '').trim(),
          duration: String(med.duration ?? '').trim(),
          instructions: med.instructions != null ? String(med.instructions).trim() : '',
          quantity: typeof med.quantity === 'number' ? med.quantity : (parseInt(med.quantity, 10) || 1),
          unit: med.unit || 'tablets',
          beforeMeal: !!med.beforeMeal,
          afterMeal: !!med.afterMeal
        }));
      
      const prescriptionData = {
        ...formData,
        doctor: formData.doctor || user?._id,
        medications: cleanMedications
      };
      
      await onAdd(prescriptionData);
      handleClose();
    } catch (err) {
      console.error('Error creating prescription:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setFormData({
      patient: '',
      appointment: '',
      doctor: '',
      diagnosis: '',
      medications: [{
        drug: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
        quantity: 1,
        unit: 'tablets',
        beforeMeal: false,
        afterMeal: false
      }],
      instructions: '',
      followUpDate: '',
      notes: ''
    });
    setErrors({});
    onClose();
  };
  
  const getDrugDisplayName = (drugId) => {
    const drug = drugs.find(d => d._id === drugId);
    if (!drug) return '';
    return `${drug.name} ${drug.strength} (${drug.form})`;
  };

  const handlePreview = async () => {
    const patientList = Array.isArray(patients) ? patients : (patients?.data ?? patients?.docs ?? []);
    const selectedPatient = patientList.find(p => p._id === formData.patient) || patientList.find(p => p.id === formData.patient);
    const selectedDoctor = doctors.find(d => d._id === formData.doctor);
    const draft = {
      prescriptionNumber: '(Preview)',
      date: new Date(),
      followUpDate: formData.followUpDate || undefined,
      diagnosis: formData.diagnosis,
      instructions: formData.instructions,
      patient: selectedPatient ? { name: selectedPatient.name, patientId: selectedPatient.patientId, phone: selectedPatient.phone, email: selectedPatient.email, dateOfBirth: selectedPatient.dateOfBirth } : { name: 'N/A' },
      doctor: selectedDoctor ? { name: selectedDoctor.name, email: selectedDoctor.email } : (user ? { name: user.name, email: user.email } : { name: 'N/A' }),
      medications: formData.medications
        .filter(m => m.drug)
        .map(m => {
          const drug = drugs.find(d => d._id === m.drug);
          return {
            drug: drug ? { name: drug.name, strength: drug.strength, form: drug.form } : { name: 'N/A' },
            dosage: m.dosage,
            frequency: m.frequency,
            duration: m.duration,
            instructions: m.instructions || ''
          };
        })
    };
    try {
      await openPrescriptionPDF(draft);
    } catch (err) {
      console.error('Error printing prescription:', err);
      toast.error(err?.message || formatFailedActionMessage('generate', 'prescription PDF'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PharmacyIcon color="primary" />
          Create New Prescription
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary">
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.patient}>
              <InputLabel>Patient *</InputLabel>
              <Select
                value={formData.patient}
                onChange={(e) => handleInputChange('patient', e.target.value)}
                label="Patient *"
                placeholder="Select a patient"
                disabled={loadingPatients}
              >
                {loadingPatients ? (
                  <MenuItem disabled>
                    <Typography variant="body2">Loading patients...</Typography>
                  </MenuItem>
                ) : patients.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2">No patients available</Typography>
                  </MenuItem>
                ) : (
                  patients.map((patient) => (
                    <MenuItem key={patient._id} value={patient._id}>
                      <Box>
                        <Typography variant="body1">{patient.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {patient.patientId} • Phone: {patient.phone}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.patient && (
                <Typography variant="caption" color="error">
                  {errors.patient}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>
                {formData.patient ? `Appointment for ${getSelectedPatientName()} (Optional)` : 'Appointment (Optional)'}
              </InputLabel>
              <Select
                value={formData.appointment}
                onChange={(e) => handleInputChange('appointment', e.target.value)}
                label={formData.patient ? `Appointment for ${getSelectedPatientName()} (Optional)` : 'Appointment (Optional)'}
                placeholder="Select an appointment"
                disabled={loadingAppointments}
              >
                <MenuItem value="">
                  <em>No appointment</em>
                </MenuItem>
                {loadingAppointments ? (
                  <MenuItem disabled>
                    <Typography variant="body2">Loading appointments...</Typography>
                  </MenuItem>
                ) : appointments.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2">No appointments available</Typography>
                  </MenuItem>
                ) : (
                  appointments.map((appointment) => (
                    <MenuItem key={appointment._id} value={appointment._id}>
                      <Box>
                        <Typography variant="body1">
                          {appointment.patient?.name || 'Unknown Patient'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatAppointmentDisplay(appointment)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.doctor}>
              <InputLabel>Prescribing Doctor *</InputLabel>
              <Select
                value={formData.doctor}
                onChange={(e) => handleInputChange('doctor', e.target.value)}
                label="Prescribing Doctor *"
                disabled={loadingDoctors}
              >
                {loadingDoctors ? (
                  <MenuItem disabled>
                    <Typography variant="body2">Loading doctors...</Typography>
                  </MenuItem>
                ) : doctors.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2">No doctors available</Typography>
                  </MenuItem>
                ) : (
                  doctors.map((d) => (
                    <MenuItem key={d._id} value={d._id}>
                      <Box>
                        <Typography variant="body1">
                          {d.name || d.email || 'Unknown'}
                          {d._id === user?._id && (
                            <Chip label="You" size="small" sx={{ ml: 1 }} color="primary" variant="outlined" />
                          )}
                        </Typography>
                        {d.email && d.name && (
                          <Typography variant="caption" color="textSecondary">
                            {d.email}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.doctor && (
                <Typography variant="caption" color="error">
                  {errors.doctor}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={icd10Options}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.code} - ${opt.description}`}
              value={formData.diagnosis}
              onInputChange={(_, value) => {
                handleInputChange('diagnosis', value);
                if (value && value.length >= 2) {
                  setIcd10Loading(true);
                  icd10API.search(value, 25).then((data) => {
                    setIcd10Options(data);
                    setIcd10Loading(false);
                  }).catch(() => setIcd10Loading(false));
                } else {
                  setIcd10Options([]);
                }
              }}
              onChange={(_, newValue) => {
                if (newValue && typeof newValue === 'object' && newValue.code) {
                  handleInputChange('diagnosis', `${newValue.code} - ${newValue.description}`);
                }
              }}
              loading={icd10Loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Diagnosis *"
                  error={!!errors.diagnosis}
                  helperText={errors.diagnosis}
                  placeholder="Search ICD-10 or type diagnosis (e.g. K02.1, caries)"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.code} - {option.description}</Typography>
                    {option.category && (
                      <Typography variant="caption" color="text.secondary">{option.category}</Typography>
                    )}
                  </Box>
                </li>
              )}
            />
          </Grid>
          
          {/* Medications */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="primary">
                Medications
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addMedication}
              >
                Add Medication
              </Button>
            </Box>
            
            {formData.medications.map((medication, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" color="primary">
                    Medication {index + 1}
                  </Typography>
                  {formData.medications.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeMedication(index)}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors[`medication_${index}_drug`]}>
                      <InputLabel>Drug *</InputLabel>
                      <Select
                        value={medication.drug}
                        onChange={(e) => handleMedicationChange(index, 'drug', e.target.value)}
                        label="Drug *"
                      >
                        {drugs.map((drug) => (
                          <MenuItem key={drug._id} value={drug._id}>
                            {drug.name} {drug.strength} ({drug.form})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors[`medication_${index}_drug`] && (
                        <Typography variant="caption" color="error">
                          {errors[`medication_${index}_drug`]}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Dosage *"
                      value={medication.dosage}
                      onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                      error={!!errors[`medication_${index}_dosage`]}
                      helperText={errors[`medication_${index}_dosage`]}
                      placeholder="e.g., 1 tablet, 5ml"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Frequency *"
                      value={medication.frequency}
                      onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                      error={!!errors[`medication_${index}_frequency`]}
                      helperText={errors[`medication_${index}_frequency`]}
                      placeholder="e.g., Twice daily, Every 8 hours"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Duration *"
                      value={medication.duration}
                      onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                      error={!!errors[`medication_${index}_duration`]}
                      helperText={errors[`medication_${index}_duration`]}
                      placeholder="e.g., 7 days, 2 weeks"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={medication.quantity}
                      onChange={(e) => handleMedicationChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={medication.unit}
                        onChange={(e) => handleMedicationChange(index, 'unit', e.target.value)}
                        label="Unit"
                      >
                        <MenuItem value="tablets">Tablets</MenuItem>
                        <MenuItem value="capsules">Capsules</MenuItem>
                        <MenuItem value="ml">ml</MenuItem>
                        <MenuItem value="mg">mg</MenuItem>
                        <MenuItem value="units">Units</MenuItem>
                        <MenuItem value="puffs">Puffs</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Special Instructions"
                      value={medication.instructions}
                      onChange={(e) => handleMedicationChange(index, 'instructions', e.target.value)}
                      placeholder="Any special instructions for this medication"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={medication.beforeMeal}
                            onChange={(e) => handleMedicationChange(index, 'beforeMeal', e.target.checked)}
                          />
                        }
                        label="Take before meal"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={medication.afterMeal}
                            onChange={(e) => handleMedicationChange(index, 'afterMeal', e.target.checked)}
                          />
                        }
                        label="Take after meal"
                      />
                    </Box>
                  </Grid>
                </Grid>
                
                {medication.drug && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected: {getDrugDisplayName(medication.drug)}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Grid>
          
          {/* General Instructions */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary">
              General Instructions
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="General Instructions"
              value={formData.instructions}
              onChange={(e) => handleInputChange('instructions', e.target.value)}
              placeholder="General instructions for the patient"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Follow-up Date"
              value={formData.followUpDate}
              onChange={(e) => handleInputChange('followUpDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes or comments"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handlePreview} disabled={loading} color="inherit">
          Preview PDF
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Prescription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPrescription;
